import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Globe, ChevronDown, Calculator, Shield, DollarSign } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const Navbar = () => {
  const { t, language, changeLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getLocalizedPath = (path) => `/${language}${path}`;

  const scrollToElement = (hash) => {
    let attempts = 0;
    const yOffset = -100;
    const scroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setTimeout(() => {
          const el2 = document.getElementById(hash);
          if (el2) {
            const rect = el2.getBoundingClientRect();
            if (rect.top < -50 || rect.top > 200) {
              const y2 = el2.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y2, behavior: 'smooth' });
            }
          }
        }, 600);
      } else if (attempts < 50) {
        attempts++;
        setTimeout(scroll, 100);
      }
    };
    scroll();
  };

  const navigateToTool = (path, hash) => {
    const targetPath = getLocalizedPath(path);
    const isCurrentPage = location.pathname === targetPath || location.pathname === `${targetPath}/`;
    if (isCurrentPage && hash) {
      scrollToElement(hash);
    } else {
      navigate(targetPath);
      if (hash) {
        scrollToElement(hash);
      }
    }
    setIsMobileMenuOpen(false);
  };

  const tools = [
    {
      label: t('nav.estimateDeployment'),
      shortLabel: t('nav.deploy'),
      icon: Calculator,
      colorClass: 'border border-[var(--accent-deployment-border)] bg-[var(--accent-deployment-bg)] text-[var(--accent-deployment)] hover:bg-[var(--accent-deployment)] hover:text-white',
      path: '/implementation',
      hash: 'deployment-estimator',
      testId: 'nav-btn-estimate-deployment',
    },
    {
      label: t('nav.modelRiskCoverage'),
      shortLabel: t('nav.riskShort'),
      icon: Shield,
      colorClass: 'border border-[var(--accent-coverage-border)] bg-[var(--accent-coverage-bg)] text-[var(--accent-coverage)] hover:bg-[var(--accent-coverage)] hover:text-white',
      path: '/operational-risk',
      hash: 'estimator-section',
      testId: 'nav-btn-model-risk',
    },
    {
      label: t('nav.calculateExposure'),
      shortLabel: t('nav.exposureShort'),
      icon: DollarSign,
      colorClass: 'border border-[var(--accent-exposure-border)] bg-[var(--accent-exposure-bg)] text-[var(--accent-exposure)] hover:bg-[var(--accent-exposure)] hover:text-white',
      path: '/exposure',
      hash: 'exposure-calculator',
      testId: 'nav-btn-calculate-exposure',
    },
  ];

  const languageLabels = { en: 'English', fr: 'Français', es: 'Español' };

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm'
          : 'bg-white/80 backdrop-blur-sm border-b border-slate-200/30'
      }`}
    >
      <div className="max-w-[1440px] mx-auto w-full">
        <div className="flex items-center justify-between h-[56px] md:h-[64px] px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-[42px] md:h-[48px] lg:h-[52px] w-auto" />
          </Link>

          {/* Right side — Action buttons + Language + Portal Login */}
          <div className="flex items-center gap-1.5">
            {/* Desktop action buttons */}
            <div className="hidden md:flex items-center gap-1.5">
              {tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <div key={tool.testId} className="relative group">
                    <button
                      onClick={() => navigateToTool(tool.path, tool.hash)}
                      className={`${tool.colorClass} inline-flex items-center gap-1.5 px-3 py-[6px] rounded text-[11px] font-bold tracking-wide transition-colors duration-150 whitespace-nowrap`}
                      data-testid={tool.testId}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="hidden lg:inline">{tool.label}</span>
                      <span className="lg:hidden">{tool.shortLabel}</span>
                    </button>
                    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2.5 py-1 bg-slate-900 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                      {tool.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block w-px h-5 bg-slate-200 mx-1" />

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium text-slate-400 hover:text-slate-700 rounded transition-colors"
                data-testid="language-selector"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className="hidden md:inline">{languageLabels[language]}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-1.5 w-32 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { changeLanguage(lang); setIsLangMenuOpen(false); }}
                      className={`w-full text-left px-3.5 py-2 text-xs font-medium transition-colors ${
                        language === lang ? 'text-slate-900 bg-slate-50' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      data-testid={`lang-option-${lang}`}
                    >
                      {languageLabels[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Portal Login */}
            <div className="hidden md:block relative group">
              <a href="https://portal.tapelligence.com" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center bg-[#22C55E] hover:bg-[#16A34A] text-white px-2.5 py-[5px] rounded text-[11px] font-semibold tracking-wide transition-colors duration-150"
                data-testid="portal-login-btn"
              >
                {t('nav.portalLogin')}
              </a>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2.5 py-1 bg-slate-900 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                {t('nav.portalLogin')}
              </span>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex md:hidden p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              data-testid="mobile-menu-toggle" aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-3 px-4">
            <div className="flex flex-col gap-1.5">
              {tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.testId}
                    onClick={() => navigateToTool(tool.path, tool.hash)}
                    className={`${tool.colorClass} flex items-center gap-2 px-3.5 py-2 rounded text-[13px] font-semibold transition-colors`}
                    data-testid={`mobile-${tool.testId}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tool.label}
                  </button>
                );
              })}
              <a href="https://portal.tapelligence.com" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center bg-[#0077FF] hover:bg-[#0066DD] text-white px-3.5 py-2 rounded text-[13px] font-semibold transition-colors mt-0.5"
                data-testid="mobile-portal-login"
              >
                {t('nav.portalLogin')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
